import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import PaymentMethodCard from '../../components/billing/PaymentMethodCard';
import InvoiceList from '../../components/billing/InvoiceList';
import { clientService } from '../../services';

/**
 * Billing Page
 * Manage payment methods, view invoices, and billing settings
 */

const BillingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // overview, payment-methods, invoices
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const response = await clientService.getBillingOverview();
      if (response.success) {
        const { subscription: sub, paymentMethods: pms, recentInvoices } = response.data;

        // Transform subscription data
        if (sub) {
          setSubscription({
            plan: sub.plan?.charAt(0).toUpperCase() + sub.plan?.slice(1) || 'Free',
            price: sub.price || 0,
            interval: sub.billingPeriod || 'month',
            status: sub.status || 'active',
            currentPeriodStart: sub.currentPeriodStart?.split('T')[0] || '',
            currentPeriodEnd: sub.currentPeriodEnd?.split('T')[0] || '',
            cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
          });
        }

        // Transform payment methods
        if (pms && pms.length > 0) {
          const transformedPms = pms.map((pm) => ({
            id: pm._id,
            type: pm.type || 'card',
            brand: pm.card?.brand || 'Card',
            last4: pm.card?.last4 || '****',
            expMonth: pm.card?.expiryMonth || '',
            expYear: pm.card?.expiryYear || '',
            isDefault: pm.isDefault || false,
          }));
          setPaymentMethods(transformedPms);
        }

        // Transform invoices
        if (recentInvoices && recentInvoices.length > 0) {
          const transformedInvoices = recentInvoices.map((inv) => ({
            id: inv._id,
            date: inv.createdAt?.split('T')[0] || '',
            description: inv.description || `${inv.subscription?.plan || 'Plan'} - ${inv.billingPeriod || 'Monthly'}`,
            amount: inv.total || 0,
            status: inv.status || 'pending',
            invoiceNumber: inv.invoiceNumber || '',
            pdfUrl: inv.pdfUrl || '#',
          }));
          setInvoices(transformedInvoices);
        }
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await clientService.getPaymentMethods();
      if (response.success && response.data.paymentMethods) {
        const transformedPms = response.data.paymentMethods.map((pm) => ({
          id: pm._id,
          type: pm.type || 'card',
          brand: pm.card?.brand || 'Card',
          last4: pm.card?.last4 || '****',
          expMonth: pm.card?.expiryMonth || '',
          expYear: pm.card?.expiryYear || '',
          isDefault: pm.isDefault || false,
        }));
        setPaymentMethods(transformedPms);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await clientService.getInvoices();
      if (response.success && response.data.invoices) {
        const transformedInvoices = response.data.invoices.map((inv) => ({
          id: inv._id,
          date: inv.createdAt?.split('T')[0] || '',
          description: inv.description || `${inv.subscription?.plan || 'Plan'} - ${inv.billingPeriod || 'Monthly'}`,
          amount: inv.total || 0,
          status: inv.status || 'pending',
          invoiceNumber: inv.invoiceNumber || '',
          pdfUrl: inv.pdfUrl || '#',
        }));
        setInvoices(transformedInvoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleSetDefaultPayment = async (paymentMethodId) => {
    try {
      const response = await clientService.setDefaultPaymentMethod(paymentMethodId);
      if (response.success) {
        setPaymentMethods((prev) =>
          prev.map((pm) => ({
            ...pm,
            isDefault: pm.id === paymentMethodId,
          }))
        );
      }
    } catch (error) {
      console.error('Error setting default payment:', error);
      alert(error.response?.data?.message || 'Failed to set default payment method');
    }
  };

  const handleDeletePayment = async (paymentMethodId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        const response = await clientService.deletePaymentMethod(paymentMethodId);
        if (response.success) {
          setPaymentMethods((prev) => prev.filter((pm) => pm.id !== paymentMethodId));
        }
      } catch (error) {
        console.error('Error deleting payment method:', error);
        alert(error.response?.data?.message || 'Failed to delete payment method');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'payment-methods', label: 'Payment Methods' },
    { id: 'invoices', label: 'Invoices' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 pb-20 sm:pb-4">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/client/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-2 group"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-xs sm:text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Billing & Subscription</h1>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1">Manage your subscription and payment methods</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/client/pricing')}>
            View Plans
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200 mb-6">
          <div className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  pb-2 px-1 font-medium text-xs transition-colors relative
                  ${
                    activeTab === tab.id
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Current Subscription */}
            <Card title="Current Subscription" padding="md">
              {subscription ? (
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base sm:text-xl font-bold text-neutral-900">{subscription.plan} Plan</h3>
                    <Badge variant={subscription.status === 'active' ? 'success' : 'warning'}>
                      {subscription.status}
                    </Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-neutral-600 mb-3">
                    ₹{subscription.price}/{subscription.interval}
                  </p>

                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-600">Billing Period:</span>
                      <span className="font-medium text-neutral-900">
                        {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-600">Next Payment:</span>
                      <span className="font-medium text-neutral-900">
                        ₹{subscription.price} on {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    </div>
                  </div>

                  {subscription.cancelAtPeriodEnd && (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-xs sm:text-sm text-orange-900">
                        Your subscription will be cancelled on {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Button variant="primary" onClick={() => navigate('/client/pricing')}>
                    Change Plan
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/client/subscription')}>
                    Manage Subscription
                  </Button>
                </div>
              </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                    <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-sm sm:text-lg font-semibold text-neutral-900 mb-2">Free Plan</h3>
                  <p className="text-xs sm:text-sm text-neutral-600 mb-4">
                    You're currently on the Free plan. Upgrade to unlock more features!
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <Button variant="primary" onClick={() => navigate('/client/pricing')}>
                      View Plans & Upgrade
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Payment Method Summary */}
            <Card title="Payment Method" padding="lg">
              {paymentMethods.length > 0 ? (
                <div>
                  {paymentMethods
                    .filter((pm) => pm.isDefault)
                    .map((pm) => (
                      <div key={pm.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-8 bg-neutral-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-neutral-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path
                                fillRule="evenodd"
                                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-neutral-900">
                              {pm.brand} •••• {pm.last4}
                            </p>
                            <p className="text-xs sm:text-sm text-neutral-600">
                              Expires {pm.expMonth}/{pm.expYear}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('payment-methods')}>
                          Manage
                        </Button>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="py-2">
                  <p className="text-xs sm:text-sm text-neutral-600">
                    Payments are securely handled through Dodo Payments. Your payment method is saved when you subscribe to a plan.
                  </p>
                </div>
              )}
            </Card>

            {/* Recent Invoices */}
            <Card title="Recent Invoices" padding="lg">
              {invoices.length > 0 ? (
                <div>
                  <div className="space-y-3">
                    {invoices.slice(0, 3).map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between py-2">
                        <div>
                          <p className="font-medium text-neutral-900">{invoice.description}</p>
                          <p className="text-xs sm:text-sm text-neutral-600">{formatDate(invoice.date)}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-neutral-900">₹{invoice.amount.toFixed(2)}</span>
                          <Badge variant="success">{invoice.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('invoices')}>
                      View All Invoices →
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-center text-neutral-600 py-4">No invoices yet</p>
              )}
            </Card>
          </div>
        )}

        {/* Payment Methods Tab */}
        {activeTab === 'payment-methods' && (
          <div>
            <Card title="Payment Methods" padding="lg">
              {paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {paymentMethods.map((pm) => (
                    <PaymentMethodCard
                      key={pm.id}
                      paymentMethod={pm}
                      isDefault={pm.isDefault}
                      onSetDefault={handleSetDefaultPayment}
                      onDelete={handleDeletePayment}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-semibold text-blue-900 mb-1">Secure Payment Processing</p>
                        <p className="text-xs sm:text-sm text-blue-700">
                          Payment methods are securely managed through our payment partner, Dodo Payments. When you subscribe or upgrade your plan, you'll be redirected to a secure checkout page to enter your payment details.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    Your payment information is never stored on our servers. To add or update a payment method, choose a plan and complete the secure checkout process.
                  </p>
                  <Button variant="primary" onClick={() => navigate('/client/pricing')}>
                    View Plans
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <Card title="Billing History" padding="lg">
            <InvoiceList invoices={invoices} />
          </Card>
        )}
      </div>

      {/* Add Payment Method Modal - Informational (PCI compliant) */}
      <Modal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        title="Payment Methods"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-blue-900 mb-1">Secure Payment Processing</p>
                <p className="text-xs sm:text-sm text-blue-700">
                  Payment methods are securely managed through our payment partner, Dodo Payments. When you upgrade your plan, you'll be redirected to a secure checkout page to enter your payment details.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-neutral-600">
            Your payment information is never stored on our servers. To add or update a payment method, simply choose a plan and complete the secure checkout process.
          </p>

          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={() => setShowAddPaymentModal(false)} fullWidth>
              Close
            </Button>
            <Button variant="primary" onClick={() => { setShowAddPaymentModal(false); navigate('/client/pricing'); }} fullWidth>
              View Plans
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default BillingPage;
